export interface OAuthAuthorizationRequestConfig {
  readonly authorizationEndpoint: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly scopes: readonly string[];
}

export interface OAuthAuthorizationRequest {
  readonly url: string;
  readonly state: string;
  readonly codeVerifier: string;
}

export interface OAuthCallbackResult {
  readonly code: string;
  readonly state: string;
}

export interface TransientAuthorizationRequest {
  readonly state: string;
  readonly codeVerifier: string;
  readonly redirectUri: string;
  readonly createdAt: number;
}

export interface TransientAuthorizationRequestStore {
  save(request: TransientAuthorizationRequest): void;
  consume(state: string): TransientAuthorizationRequest | undefined;
}

function requireHttpsUrl(value: string, label: string): URL {
  const url = new URL(value);
  if (url.protocol !== 'https:') {
    throw new Error(`${label} must use HTTPS.`);
  }
  if (url.username.length > 0 || url.password.length > 0 || url.hash.length > 0) {
    throw new Error(`${label} must not contain credentials or a fragment.`);
  }
  return url;
}

function requireOpaqueValue(value: string, label: string): string {
  if (!/^[A-Za-z0-9._~-]{16,256}$/u.test(value)) {
    throw new Error(`${label} must contain 16-256 safe opaque characters.`);
  }
  return value;
}

export function generateOAuthState(cryptoApi: Crypto = globalThis.crypto): string {
  const bytes = new Uint8Array(32);
  cryptoApi.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function buildAuthorizationUrl(
  config: OAuthAuthorizationRequestConfig,
  state: string,
  codeChallenge: string,
): string {
  const authorizationEndpoint = requireHttpsUrl(
    config.authorizationEndpoint,
    'Authorization endpoint',
  );
  requireHttpsUrl(config.redirectUri, 'OAuth redirect URI');
  requireOpaqueValue(state, 'OAuth state');
  requireOpaqueValue(codeChallenge, 'PKCE code challenge');

  if (config.clientId.trim().length === 0) {
    throw new Error('OAuth client ID is required.');
  }
  if (config.scopes.length === 0 || config.scopes.some((scope) => scope.trim().length === 0)) {
    throw new Error('At least one non-empty OAuth scope is required.');
  }

  authorizationEndpoint.search = '';
  authorizationEndpoint.searchParams.set('response_type', 'code');
  authorizationEndpoint.searchParams.set('client_id', config.clientId);
  authorizationEndpoint.searchParams.set('redirect_uri', config.redirectUri);
  authorizationEndpoint.searchParams.set('scope', config.scopes.join(' '));
  authorizationEndpoint.searchParams.set('state', state);
  authorizationEndpoint.searchParams.set('code_challenge_method', 'S256');
  authorizationEndpoint.searchParams.set('code_challenge', codeChallenge);

  return authorizationEndpoint.toString();
}

export function parseAuthorizationCallback(
  callbackUrl: string,
  expectedState: string,
): OAuthCallbackResult {
  const callback = requireHttpsUrl(callbackUrl, 'OAuth callback URL');
  const error = callback.searchParams.get('error');
  if (error !== null) {
    const description = callback.searchParams.get('error_description');
    throw new Error(
      `OAuth authorization failed: ${error}${description === null ? '' : ` (${description})`}`,
    );
  }

  const code = callback.searchParams.get('code');
  const state = callback.searchParams.get('state');
  if (code === null || code.length === 0 || state === null || state.length === 0) {
    throw new Error('OAuth callback is missing authorization code or state.');
  }
  if (state !== expectedState) {
    throw new Error('OAuth callback state does not match the pending authorization request.');
  }

  return { code, state };
}
